/* Build trigger 2026-05-23 — force Netlify to rebuild against latest main. */
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './App.css';
import ProductRegistry from './components/ProductRegistry';
import SupplierRegistry from './components/SupplierRegistry';
import ClientRegistry from './components/ClientRegistry';
import WarehouseRegistry from './components/WarehouseRegistry';
import CustomsBrokerRegistry from './components/CustomsBrokerRegistry';
import QualityControlRegistry from './components/QualityControlRegistry';
import CertifierRegistry from './components/CertifierRegistry';
import GrowerRegistry from './components/GrowerRegistry';
import CertificationRegistry from './components/CertificationRegistry';
import SeasonRegistry from './components/SeasonRegistry';
import ExportCertificateRegistry from './components/ExportCertificateRegistry';
import ShipmentRegistry from './components/ShipmentRegistry';
import FobPricesRegistry from './components/FobPricesRegistry';
import ForecastRegistry from './components/ForecastRegistry';
import WeatherWatchRegistry from './components/WeatherWatchRegistry';
import MarketNewsRegistry from './components/MarketNewsRegistry';
import HolidayImpactRegistry from './components/HolidayImpactRegistry';
import CurrencyFuelRegistry from './components/CurrencyFuelRegistry';
import AIAnalystLog from './components/AIAnalystLog';
import AccuracyDashboard from './components/AccuracyDashboard';
import ApiSettingsRegistry from './components/ApiSettingsRegistry';
import PalletBreakdown from './components/PalletBreakdown';
import ShipperPortal from './components/ShipperPortal';
import TrackingDriver from './components/TrackingDriver';
import GPSTracking from './components/GPSTracking';
import ShipperBrokersPortal from './components/ShipperBrokersPortal';
import ShipperSettings from './components/ShipperSettings';
import BillRegistry from './components/BillRegistry';
import InvoiceRegistry from './components/InvoiceRegistry';
import SweetLeafPortal from './components/SweetLeafPortal';
import { SL_ISOTIPO_B64 } from './components/brandLogos';
import ProfitTracker from './components/ProfitTracker';
import FeeCatalog from './components/FeeCatalog';
import ExpenseBillRegistry from './components/ExpenseBillRegistry';
import SalesReports from './components/SalesReports';
import ReportGenerator from './components/ReportGenerator';
import PaymentRegistry from './components/PaymentRegistry';
import GlobalSearch from './components/GlobalSearch';
import Dashboard from './components/Dashboard';
import ImportFromExcel from './components/ImportFromExcel';
import Settlements from './components/Settlements';
import CompanyProfile from './components/CompanyProfile';
import FixedPricingRegistry from './components/FixedPricingRegistry';
import ConsignmentPricingRegistry from './components/ConsignmentPricingRegistry';
import CombinedWeeklySummary from './components/CombinedWeeklySummary';
import SeasonComparison from './components/SeasonComparison';
import BackupRestore from './components/BackupRestore';
import SupabaseMigration from './components/SupabaseMigration';
import OrganicAuditRegistry from './components/OrganicAuditRegistry';
import USDALookup from './components/USDALookup';
import { installSaveCounter, exportAndDownload, shouldPromptForBackup, getBackupStatus, dismissBackupReminder } from './components/gfiBackup';
import LoginPage from './components/LoginPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import RegisterPage from './components/RegisterPage';
import PasswordResetPage from './components/PasswordResetPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import ShipperPortalShell from './components/ShipperPortalShell';
import AdvisorPortalShell from './components/AdvisorPortalShell';
import UserManagement from './components/UserManagement';
import { supabase } from './supabaseClient';
import { loadFromSupabase } from './supabaseDB';
import { getCurrentProfile, clearProfileCache } from './userProfile';

/* Admin view of the Shipper Portal. ShipperPortal resolves "the first
   shipper in the list" when no shipper is locked, which made the admin
   view open on an arbitrary shipper (and silently change when the
   supplier load order changed). This wrapper adds an explicit shipper
   picker and passes the chosen shipper to ShipperPortal via
   lockSupplierId, so the admin always controls which shipper they see.
   The choice persists across navigation as a UI preference
   (localStorage), not as a source of truth — data still loads from
   Supabase. */
function AdminShipperPortal({ lang, profile }) {
  const [shippers, setShippers] = useState([]);
  const [selectedId, setSelectedId] = useState(function () {
    try { return localStorage.getItem("gfi-admin-shipper-view-id") || ""; } catch (e) { return ""; }
  });
  useEffect(function () {
    let alive = true;
    loadFromSupabase("suppliers").then(function (rows) {
      if (!alive) return;
      const list = (rows || []).filter(function (s) { return s.supplierType === "shipper"; });
      list.sort(function (a, b) { return String(a.shortName || a.fullName || "").localeCompare(String(b.shortName || b.fullName || "")); });
      setShippers(list);
      setSelectedId(function (prev) {
        if (prev && list.some(function (s) { return String(s.id) === String(prev); })) return prev;
        return list.length ? String(list[0].id) : "";
      });
    });
    return function () { alive = false; };
  }, []);
  function pick(id) {
    setSelectedId(id);
    try { localStorage.setItem("gfi-admin-shipper-view-id", id); } catch (e) {}
  }
  const current = shippers.find(function (s) { return String(s.id) === String(selectedId); }) || null;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--color-border)" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-muted)" }}>{lang === "es" ? "Ver shipper:" : "View shipper:"}</span>
        <select value={selectedId} onChange={function (e) { pick(e.target.value); }} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: 13, fontWeight: 600, minWidth: 220 }}>
          {shippers.length === 0 && <option value="">{lang === "es" ? "Cargando…" : "Loading…"}</option>}
          {shippers.map(function (s) { return <option key={s.id} value={s.id}>{s.shortName || s.fullName || s.id}</option>; })}
        </select>
        {current && current.logoUrl ? <img src={current.logoUrl} alt="" style={{ height: 28, maxWidth: 70, objectFit: "contain", marginLeft: "auto" }} /> : null}
      </div>
      {selectedId
        ? <ShipperPortal key={selectedId} lang={lang} lockSupplierId={selectedId} profile={profile} />
        : <div style={{ padding: 24, color: "var(--color-text-muted)" }}>{lang === "es" ? "Selecciona un shipper." : "Select a shipper."}</div>}
    </div>
  );
}

/* ===== SVG ICONS =====
   Historical icon dictionary used by the legacy sidebar. The current
   top-nav doesn't reference it but we keep the const around so it
   can be reused by future modules without re-deriving the SVG
   paths. The eslint-disable below silences the unused-var warning. */
// eslint-disable-next-line no-unused-vars
const I = {
  dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  products: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  suppliers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5a1 1 0 01-1 1h-1"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  clients: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  warehouses: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  brokers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><line x1="12" y1="12" x2="12" y2="12.01"/></svg>,
  growers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>,
  certs: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/></svg>,
  seasons: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  shipments: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  costs: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
  expenses: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  po: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  prices: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  pallet: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="5" rx="1"/><rect x="4" y="12" width="4" height="5"/><rect x="10" y="12" width="4" height="5"/><rect x="16" y="12" width="4" height="5"/><line x1="2" y1="17" x2="22" y2="17"/></svg>,
  invoice: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="12" y2="17"/></svg>,
  payments: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M22 10H2"/><circle cx="17" cy="14" r="1.5"/></svg>,
  packing: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
  ticket: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5v2"/><path d="M15 11v2"/><path d="M15 17v2"/><path d="M5 5h14a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 000-4V7a2 2 0 012-2z"/></svg>,
  report: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>,
  profit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  chart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  bell: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
  settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
};

/* Icon-only brand mark — no "GFI Nexus" wordmark, no company name/logo
   next to it (those moved out, see the header below and
   .main-header-greeting, 2026-09-12) so it leaves the rest of
   .top-nav's width to the menu. Size kept at its original 88px per
   Arturo — only the text next to it was removed, not the icon itself. */
function GFILogo() {
  return (
    <svg width="88" height="88" viewBox="-55 -55 110 110" style={{ flexShrink: 0 }}>
      <defs><linearGradient id="logoGl" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="rgba(255,255,255,0.25)"/><stop offset="40%" stopColor="rgba(255,255,255,0.05)"/><stop offset="100%" stopColor="rgba(0,0,0,0.15)"/></linearGradient></defs>
      <rect x="-35" y="-35" width="70" height="70" rx="16" fill="#1B3A35"/>
      <rect x="-35" y="-35" width="70" height="70" rx="16" fill="url(#logoGl)"/>
      <text x="0" y="2" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="22" fontWeight="700" fill="#5BBCB0" letterSpacing="3">GFI</text>
      <rect x="-16" y="10" width="32" height="1.2" rx="0.6" fill="#3D9B8F"/>
      <text x="0" y="22" textAnchor="middle" fontFamily="'DM Sans', sans-serif" fontSize="8" fontWeight="500" fill="#3D9B8F" letterSpacing="3">NEXUS</text>
    </svg>
  );
}

/* Horizontal top-nav structure (Block 4). Six top-level entries:
   Dashboard (flat link) + 5 dropdown groups. Every module that has a
   case in renderModule() lives somewhere in this tree — nothing was
   removed even when not explicitly listed in the spec; secondary
   items (Sales Reports, Profitability, Cost Analysis) were placed
   under the closest matching group so they remain reachable. `key`
   matches the module id consumed by renderModule(); groups carry
   an `id` so the activeGroupId resolver can highlight the parent
   dropdown.

   Shipper Portal is intentionally the LAST entry so it renders at
   the far right of the menu, just before the icon strip — it's the
   shipper's isolated world and when real shipper-login arrives this
   is the entire surface a shipper user sees, so keeping it visually
   separate from the GFI-staff modules matters. */
const NAV = [
  { id: 'home', key: 'dashboard', en: 'Dashboard', es: 'Dashboard' },
  { id: 'operations', en: 'Operations', es: 'Operaciones', items: [
    { key: 'shipments',         en: 'Shipments',              es: 'Embarques' },
    { key: 'pallet_breakdown', en: 'Packing List Breakdown', es: 'Detalle Packing List' },
    { key: 'bills',             en: 'Bills',                  es: 'Bills' },
    { key: 'invoices',          en: 'Invoices',               es: 'Facturas' },
    { key: 'expense_bills',     en: 'Expense Bills',          es: 'Facturas de Gastos' },
    { key: 'seasons',           en: 'Seasons',                es: 'Temporadas' },
    { key: 'gps_tracking',      en: 'GPS Tracking',           es: 'Rastreo GPS' },
    { key: 'report_generator',  en: 'Report Generator',       es: 'Generador de Reportes' },
  ]},
  /* Forecast & Market Intelligence (fix-dashboard.md Fase A) — FOB
     Prices moved here from Operations (same component, just a new
     home in the menu); the other 7 are Coming-soon placeholders
     until built per CLAUDE.md's "FOB PRICE FORECAST v2" roadmap. */
  { id: 'forecast_intelligence', en: 'Forecast & Market Intelligence', es: 'Proyecciones e Inteligencia de Mercado', items: [
    { key: 'fob_prices',          en: 'FOB Prices',            es: 'Precios FOB' },
    { key: 'forecast',            en: 'Forecast',               es: 'Proyecciones' },
    { key: 'weather_watch',       en: 'Weather Watch',          es: 'Monitor de Clima' },
    { key: 'market_news',         en: 'Market News',            es: 'Noticias de Mercado' },
    { key: 'holiday_impact',      en: 'Holiday Impact',         es: 'Impacto de Feriados' },
    { key: 'currency_fuel',       en: 'Currency & Fuel',        es: 'Divisa y Combustible' },
    { key: 'ai_analyst_log',      en: 'AI Analyst Log',         es: 'Registro Analista IA' },
    { key: 'accuracy_dashboard',  en: 'Accuracy Dashboard',     es: 'Dashboard de Precisión' },
  ]},
  /* 2026-10-08, Arturo: "ahora todas las PO de cualquier cliente las
     genero en un solo modulo llamado fixed que simplemente deberia
     llamarse weekly PO's, el consigment pricing ya quedo inservible,
     no lo borres solo que no este visible, asi como tampoco el
     weekly summary que combinaba ambos" — Consignment Pricing and
     Weekly Summary are retired from the sidebar (Fixed Pricing's own
     screen now captures every term, Fixed/Semi-fixed/Consignment
     alike — see FixedPricingRegistry.js's 2026-09-17/18 merge).
     Neither entry is deleted: ConsignmentPricingRegistry.js and
     CombinedWeeklySummary.js, their imports below, and their
     renderModule() cases all stay exactly as they are — just no
     longer reachable from this nav. */
  { id: 'finance', en: 'Finance', es: 'Finanzas', items: [
    { key: 'payments',            en: 'Payment Registry',     es: 'Registro de Pagos' },
    { key: 'settlements',         en: 'Settlements',          es: 'Liquidaciones' },
    { key: 'profit_tracking',     en: 'Profit Tracker',       es: 'Seguimiento de Ganancia' },
    { key: 'fixed_pricing',       en: "Weekly PO's",          es: "PO's Semanales" },
    { key: 'season_comparison',   en: 'Season Comparison',    es: 'Comparativo de Temporadas' },
  ]},
  { id: 'catalogs', en: 'Catalogs', es: 'Catálogos', items: [
    { key: 'products',    en: 'Products',     es: 'Productos' },
    { key: 'suppliers',   en: 'Suppliers',    es: 'Proveedores' },
    { key: 'clients',     en: 'Clients',      es: 'Clientes' },
    { key: 'warehouses',  en: 'Warehouses',   es: 'Bodegas' },
    { key: 'brokers',     en: 'Brokers',      es: 'Agentes Aduanales' },
    { key: 'quality_control', en: 'Quality Control', es: 'Control de Calidad' },
    { key: 'certifiers',  en: 'Certifiers',   es: 'Certificadoras' },
    { key: 'growers',     en: 'Growers',      es: 'Productores' },
    { key: 'fee_catalog', en: 'Fee Catalog',  es: 'Catálogo de Gastos' },
  ]},
  { id: 'compliance', en: 'Compliance', es: 'Cumplimiento', items: [
    { key: 'export_certs',    en: 'Export Certificates', es: 'Certificados de Exportación' },
    { key: 'certifications',  en: 'Certifications',      es: 'Certificaciones' },
    { key: 'report_organic',  en: 'Organic Audit',       es: 'Auditoría Orgánica' },
    { key: 'usda_lookup',     en: 'USDA Lookup',         es: 'Consulta USDA' },
  ]},
  { id: 'settings', en: 'Settings', es: 'Configuración', items: [
    { key: 'company_profile',     en: 'Company Profile',     es: 'Perfil de Empresa' },
    { key: 'user_management',     en: 'User Management',     es: 'Gestión de Usuarios' },
    { key: 'shipper_settings',    en: 'Shipper Settings',    es: 'Configuración del Exportador' },
    { key: 'storage',             en: 'Storage Manager',     es: 'Almacenamiento' },
    { key: 'backup',              en: 'Backup & Restore',    es: 'Respaldo y Restauración' },
    { key: 'import_excel',        en: 'Import from Excel',   es: 'Importar desde Excel' },
    { key: 'supabase_migration',  en: 'Migrate to Supabase', es: 'Migrar a Supabase' },
    { key: 'api_settings',        en: 'API Keys & Data Sources', es: 'Claves API y Fuentes de Datos' },
  ]},
];

/* Shipper Portal lives OUTSIDE the main NAV. It renders as a
   right-anchored dropdown inside the icon strip (top-nav-right),
   immediately before the backup icon — see the explicit block in
   the App's JSX below. Keeping it out of the main NAV stops it
   from drifting into Operations or any other dropdown. */
const SHIPPER_PORTAL_GROUP = {
  id: 'shipper_portal_group',
  en: 'Shipper Portal',
  es: 'Portal del Exportador',
  items: [
    { key: 'shipper_shipments',    en: 'Shipments',       es: 'Embarques' },
    { key: 'shipper_gps', en: 'GPS Tracking',    es: 'Rastreo GPS' },
    { key: 'shipper_brokers',      en: 'Customs Brokers', es: 'Agentes Aduanales' },
  ],
};

const SWEETLEAF_GROUP = {
  id: 'sweetleaf_group',
  en: 'SweetLeaf',
  es: 'SweetLeaf',
  items: [
    { key: 'sl_invoices', en: 'Invoices', es: 'Facturas' },
    { key: 'sl_payments', en: 'Payments', es: 'Pagos' },
    { key: 'sl_settings', en: 'Settings', es: 'Configuración' },
  ],
};

/* Flat lookup for module label resolution (used by ComingSoon fallback).
   Includes SHIPPER_PORTAL_GROUP + SWEETLEAF_GROUP so the placeholder
   still resolves the right label for portal-side modules. */
const NAV_FLAT = (function () {
  var acc = {};
  NAV.forEach(function (g) {
    if (g.items) g.items.forEach(function (i) { acc[i.key] = i; });
    else acc[g.key] = g;
  });
  SHIPPER_PORTAL_GROUP.items.forEach(function (i) { acc[i.key] = i; });
  SWEETLEAF_GROUP.items.forEach(function (i) { acc[i.key] = i; });
  return acc;
})();

/* Auto sign-out after this many ms of user inactivity.
   Default 60 minutes. To change, edit the first number below. */
const INACTIVITY_LIMIT_MS = 60 * 60 * 1000;

// eslint-disable-next-line no-unused-vars
function DashboardPlaceholder({ lang }) {
  return (
    <div className="dashboard-placeholder">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
      <h2>Dashboard</h2>
      <p>{lang === 'es'
        ? 'El dashboard principal se construirá cuando los módulos de operaciones estén listos. Aquí verás embarques activos, certificados por vencer, facturas pendientes y ventas de la semana.'
        : 'The main dashboard will be built once the operations modules are ready. Here you\'ll see active shipments, expiring certificates, pending invoices, and weekly sales.'
      }</p>
    </div>
  );
}

function ComingSoon({ label }) {
  return (
    <div className="dashboard-placeholder">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
      <h2>{label}</h2>
      <p style={{ fontSize: 13, color: '#6a7a90' }}>Coming soon</p>
    </div>
  );
}

/* ===== STORAGE MANAGER ===== */
function StorageManager({ lang }) {
  var [data, setData] = useState([]);
  var [refreshKey, setRefreshKey] = useState(0);
  useEffect(function() {
    var items = [];
    try {
      for (var k in localStorage) {
        if (localStorage.hasOwnProperty(k)) {
          var val = localStorage[k] || '';
          var sizeKB = (val.length / 1024).toFixed(1);
          var count = '';
          try { var parsed = JSON.parse(val); if (Array.isArray(parsed)) count = parsed.length + (lang === 'es' ? ' registros' : ' records'); } catch(e) {}
          items.push({ key: k, sizeKB: parseFloat(sizeKB), count: count });
        }
      }
    } catch(e) {}
    items.sort(function(a, b) { return b.sizeKB - a.sizeKB; });
    setData(items);
  }, [refreshKey, lang]);
  var totalKB = data.reduce(function(s, d) { return s + d.sizeKB; }, 0).toFixed(1);
  var pct = Math.min(100, Math.round((totalKB / 5120) * 100));
  var clearKey = function(key) { if (window.confirm((lang === 'es' ? '\u00bfEliminar ' : 'Delete ') + key + '?')) { localStorage.removeItem(key); setRefreshKey(function(v) { return v + 1; }); } };
  var t = lang === 'es' ? { title: 'Almacenamiento', sub: 'Gestiona el espacio de datos local', key: 'Clave', size: 'Tama\u00f1o', records: 'Registros', del: 'Eliminar', total: 'Total usado', of: 'de', warn: 'Almacenamiento casi lleno. Elimina datos de prueba o exporta antes de continuar.' } : { title: 'Storage Manager', sub: 'Manage local data storage', key: 'Key', size: 'Size', records: 'Records', del: 'Delete', total: 'Total used', of: 'of', warn: 'Storage nearly full. Delete test data or export before continuing.' };
  var th = { padding: '10px 8px', textAlign: 'left', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-secondary)', borderBottom: '2px solid var(--color-border)' };
  return (<div>
    <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px' }}>{t.title}</h2>
    <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '0 0 14px' }}>{t.sub}</p>
    {pct > 85 && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#DC2626', fontSize: 12, fontWeight: 600, marginBottom: 14 }}>{"\u26a0"} {t.warn}</div>}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--color-border)' }}><div style={{ width: pct + '%', height: '100%', borderRadius: 4, background: pct > 90 ? '#DC2626' : pct > 70 ? '#D97706' : 'var(--color-accent)' }} /></div>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600 }}>{totalKB} KB {t.of} 5,120 KB ({pct}%)</span>
    </div>
    <div style={{ borderRadius: 10, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>{t.key}</th><th style={{ ...th, textAlign: 'right' }}>{t.size}</th><th style={th}>{t.records}</th><th style={{ ...th, textAlign: 'center' }}>{t.del}</th></tr></thead>
        <tbody>{data.map(function(d) {
          var isGfi = d.key.startsWith('gfi-');
          return <tr key={d.key} style={{ borderBottom: '1px solid var(--color-border)' }}>
            <td style={{ padding: '8px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", fontWeight: isGfi ? 600 : 400, color: isGfi ? 'var(--color-text)' : 'var(--color-text-muted)' }}>{d.key}</td>
            <td style={{ padding: '8px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600, color: d.sizeKB > 500 ? '#DC2626' : d.sizeKB > 100 ? '#D97706' : 'var(--color-text-secondary)' }}>{d.sizeKB} KB</td>
            <td style={{ padding: '8px', fontSize: 11, color: 'var(--color-text-muted)' }}>{d.count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}><button onClick={function() { clearKey(d.key); }} style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 9, fontWeight: 600 }}>{"\u2715"}</button></td>
          </tr>;
        })}</tbody>
      </table>
    </div>
  </div>);
}

/* Public driver tracking page — bypasses every auth gate, the
   shell, the sidebar, and the language preference. The driver
   opens this URL on his phone from a WhatsApp link; he is not a
   GFI staff user and must never hit the LoginPage. The route is
   matched by raw URL prefix instead of a router library since this
   project doesn't ship react-router. Detected once at mount; if the
   driver navigates back the page reloads anyway (mobile WhatsApp
   opens a fresh tab). */
function getDriverTrackingToken() {
  try {
    var p = window.location.pathname || "";
    var m = p.match(/^\/track\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch (e) { return null; }
}

/* Top-level dispatcher. Splits the public driver page from the
   staff shell so neither component contains a conditional hook
   (which `react-hooks/rules-of-hooks` would refuse to compile).
   The pathname doesn't change without a full reload, so the choice
   between branches is stable across the component's lifetime. */
function App() {
  var driverToken = getDriverTrackingToken();
  if (driverToken) return <TrackingDriver token={driverToken} />;
  return <AppShell />;
}

function AppShell() {
  /* 2026-10-20, Arturo: "quita la funcion de cambiar colores
     manualmente" — the 3-dot theme picker is gone; `theme` is still
     read (drives data-theme + every CSS var across the app), just
     fixed to whatever's saved/default, never changed in-session, so
     no setter is kept. */
  const [theme] = useState(() => localStorage.getItem('gfi-theme') || 'mist');
  const [module, setModule] = useState(() => {
    var saved = localStorage.getItem('gfi-module');
    /* 'fobPrices' was the module's key before it was renamed to 'fob_prices'
       (Sub-fase D.1). Browsers that saved the old key before the rename
       would otherwise get stuck on a raw-key "Coming soon" placeholder
       forever, since it never matches a real switch case again. */
    if (saved === 'fobPrices') return 'fob_prices';
    return saved || 'dashboard';
  });
  const [pendingShipmentId, setPendingShipmentId] = useState(null);
  const [returnModule, setReturnModule] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem('gfi-lang') || 'es');
  const [openDropdown, setOpenDropdown] = useState(null);
  /* Mobile nav drawer (2026-09-12, Arturo) — the old mobile approach
     (`.top-nav { overflow-x: auto }`, scrolling the pill menu
     horizontally) left most of the app unreachable in practice: on a
     phone screen only 2-3 pills fit before the rest scrolled off with
     no visible scrollbar or hint, and Arturo could only ever reach
     Dashboard + whatever happened to already be in view. Replaced
     below 900px with a real hamburger -> full drawer listing every
     group (including SweetLeaf/Shipper Portal, normally in
     .top-nav-right) as a tap-to-expand accordion, so every module is
     reachable in at most 2 taps regardless of screen width. */
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState(null);
  /* Per-group trigger refs — needed so the dropdown can portal to
     document.body and still position itself directly under the
     trigger button. Keyed by group.id; populated via callback refs
     on the .nav-group divs. Escaping the .top-nav + .main-body
     stacking contexts via portal is the only reliable way to keep
     the dropdown above page content on iOS Safari mobile, where
     `position: fixed` inside a `position: sticky` ancestor still
     paints below sibling stacking contexts (.main-body's
     `isolation: isolate` was the wedge). */
  const navTriggerRefs = useRef({});
  /* Delayed close for top-nav dropdowns. Without this, leaving the
     trigger button to move down into the (body-portaled) dropdown
     panel fires `top-nav-menu`'s onMouseLeave before the cursor
     reaches the dropdown — the dropdown is OUTSIDE the menu in the
     DOM tree, so a normal mouseleave can't tell the two regions are
     logically connected. The timer gives the cursor a 220 ms window
     to cross the gap; onMouseEnter on the dropdown cancels it. */
  const navCloseTimerRef = useRef(null);
  const scheduleNavClose = () => {
    if (navCloseTimerRef.current) clearTimeout(navCloseTimerRef.current);
    navCloseTimerRef.current = setTimeout(() => {
      navCloseTimerRef.current = null;
      setOpenDropdown(null);
    }, 220);
  };
  const cancelNavClose = () => {
    if (navCloseTimerRef.current) { clearTimeout(navCloseTimerRef.current); navCloseTimerRef.current = null; }
  };
  const [searchOpen, setSearchOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef(null);

  /* Auth state — undefined while we load the initial session, null when
     signed out, an object when signed in. App shell is gated below on
     this; LoginPage swaps in when null.

     Profile / role — once we have a session, fetch the user_profiles
     row. Shape is `undefined` (loading), `null` (no profile yet — fall
     back to admin for the migration window), or `{ role, ... }`. The
     shipper-role gate below swaps in ShipperPortalShell when role
     === 'shipper'.

     IMPORTANT: We must NOT setState on every TOKEN_REFRESHED event.
     Supabase auto-refreshes the access token whenever the tab regains
     focus after sitting idle past the token TTL, which fires
     onAuthStateChange with a brand-new session object referencing the
     SAME user. The previous version called setSession(s) + cleared
     profile + setProfile(undefined) on every fire — that flips the
     role gate back to the loading splash for one render cycle, which
     unmounts the entire shell including any open DraggableModal /
     form. Result: every tab switch wiped in-flight work.

     The fix below routes events through a sessionRef:
       - SIGNED_OUT          → force a remount (login screen).
       - First INITIAL_SESSION / SIGNED_IN / any event with a new
         user.id → real auth change, do the full setSession +
         clear-profile dance.
       - TOKEN_REFRESHED / USER_UPDATED / any event whose user.id
         matches the current session → silent. The Supabase client's
         internal store already has the new token; we don't need to
         touch React state. */
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(undefined);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const sessionRef = useRef(null);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      sessionRef.current = data.session || null;
      setSession(data.session || null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (!mounted) return;

      if (event === 'PASSWORD_RECOVERY') {
        /* User arrived via the email reset link. Supabase has created
           a temporary session; show the set-new-password screen
           instead of the normal shell. Keep the session so
           updateUser() is authorized. */
        if (s) {
          sessionRef.current = s;
          setSession(s);
        }
        setRecoveryMode(true);
        return;
      }

      if (event === 'SIGNED_OUT' || !s) {
        sessionRef.current = null;
        clearProfileCache();
        setSession(null);
        setProfile(undefined);
        return;
      }

      const newUid = (s && s.user && s.user.id) || null;
      const oldUid = (sessionRef.current && sessionRef.current.user && sessionRef.current.user.id) || null;

      if (newUid !== oldUid) {
        /* Genuine auth change: first sign-in after a fresh page load,
           or the user swapped accounts. Trigger the full mount path. */
        sessionRef.current = s;
        clearProfileCache();
        setSession(s);
        setProfile(undefined);
      } else {
        /* Same user, fresh token (TOKEN_REFRESHED) or USER_UPDATED.
           Quietly update the ref. The Supabase client's internal
           session object already reflects the new tokens, so any
           subsequent supabase.* call sees them. We deliberately skip
           setSession / setProfile so React doesn't re-render the
           shell — open modals and forms keep their state. */
        sessionRef.current = s;
      }
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  /* Mount-time safety net: if Supabase strips the recovery hash
     before our auth listener attaches, this catches type=recovery
     in the URL and forces the reset screen. Harmless if the hash
     is already gone — the event path will set recoveryMode too. */
  useEffect(() => {
    try {
      const h = window.location.hash || '';
      if (h.indexOf('type=recovery') !== -1) setRecoveryMode(true);
    } catch (e) {}
  }, []);

  /* Auto sign-out after INACTIVITY_LIMIT_MS of no user input.
     We track the last-activity timestamp in a closure ref so the
     interval doesn't have to re-bind every render, listen for any
     pointer/keyboard/scroll/touch event passively to bump it, and
     check every 15 s. signOut is fire-and-forget; the auth listener
     above handles the SIGNED_OUT event and swaps to LoginPage. */
  useEffect(() => {
    if (!session) return;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const id = setInterval(() => {
      if (Date.now() - last > INACTIVITY_LIMIT_MS) {
        supabase.auth.signOut().catch(() => {});
      }
    }, 15000);
    return () => {
      clearInterval(id);
      events.forEach((ev) => window.removeEventListener(ev, bump));
    };
  }, [session]);

  /* Close the logout confirm modal with the Escape key. */
  useEffect(() => {
    if (!logoutConfirmOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setLogoutConfirmOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [logoutConfirmOpen]);

  /* Warn the user before they close the tab / window while signed
     in. The browser shows its own generic dialog — modern browsers
     ignore custom messages, so the returnValue string is only a
     formality required by the spec. Only armed while a session
     exists so the login page itself doesn't trigger the prompt. */
  useEffect(() => {
    if (!session) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [session]);

  /* Resolve the user's profile + role whenever the session changes.
     Runs only when setSession is actually called (i.e. on real auth
     changes — token refreshes are filtered above). */
  useEffect(() => {
    if (!session) { setProfile(undefined); return; }
    let alive = true;
    getCurrentProfile(true).then(p => {
      if (!alive) return;
      /* `null` = no profile row (table missing or never inserted).
         The render gate below treats that as "admin fallback" so the
         existing shell keeps working during the migration window. */
      setProfile(p || null);
    }).catch(() => { if (alive) setProfile(null); });
    return () => { alive = false; };
  }, [session]);

  /* Close Create New dropdown on click outside */
  useEffect(() => {
    if (!createOpen) return;
    const handler = (e) => {
      if (createRef.current && !createRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setCreateOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setCreateOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [createOpen]);

  /* Close top-nav dropdown on outside tap/click. The hover-leave handler
     covers desktop; this covers touch devices where there's no
     mouseleave event. Also closes on Escape. */
  useEffect(() => {
    if (!openDropdown) return;
    const handler = (e) => {
      /* Accept clicks inside either the trigger group OR the
         portaled dropdown panel (it's no longer a descendant of
         .nav-group after the portal lift). Otherwise close. */
      if (!e.target.closest) { setOpenDropdown(null); return; }
      if (e.target.closest('.nav-group')) return;
      if (e.target.closest('.nav-dropdown')) return;
      setOpenDropdown(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenDropdown(null); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [openDropdown]);

  const goCreate = (moduleKey, hookName) => {
    setCreateOpen(false);
    if (window.__gfiNavigate) window.__gfiNavigate(moduleKey);
    if (hookName) setTimeout(() => {
      const fn = window[hookName];
      if (typeof fn === 'function') fn();
    }, 200);
  };

  const CREATE_OPTIONS = [
    { key: 'shipments', hook: '__gfiShipmentNew', en: 'New Shipment', es: 'Nuevo Embarque' },
    { key: 'bills', hook: '__gfiBillNew', en: 'New Bill', es: 'Nuevo Bill' },
    { key: 'invoices', hook: '__gfiInvoiceNew', en: 'New Invoice', es: 'Nueva Factura' },
    { key: 'expense_bills', hook: '__gfiExpenseBillNew', en: 'New Expense Bill', es: 'Nueva Factura de Gasto' },
    { key: 'payments', hook: null, en: 'New Payment', es: 'Nuevo Pago' },
  ];

  useEffect(() => { document.documentElement.setAttribute('data-theme', theme); localStorage.setItem('gfi-theme', theme); }, [theme]);
  useEffect(() => { localStorage.setItem('gfi-module', module); }, [module]);
  useEffect(() => { localStorage.setItem('gfi-lang', lang); }, [lang]);

  /* Bootstrap-prime the legacy `gfi-*` caches that many modules still
     read synchronously (seasons in particular — referenced by every
     dated module's filter and by the season label resolver). If the
     user lands on Shipments / Bills / Invoices without first opening
     SeasonRegistry, this is the only place those caches get
     populated. Runs once per signed-in session. */
  useEffect(() => {
    if (!session) return;
    let alive = true;
    const pairs = [
      ['seasons',    'gfi-seasons'],
      ['suppliers',  'gfi-suppliers'],
      ['clients',    'gfi-clients'],
      ['warehouses', 'gfi-warehouses'],
      ['products',   'gfi-products'],
    ];
    Promise.all(pairs.map(p =>
      loadFromSupabase(p[0]).then(rows => {
        if (!alive) return;
        try { localStorage.setItem(p[1], JSON.stringify(rows)); } catch (e) {}
      }).catch(() => {})
    ));
    /* Seed gfi-commodities on first sign-in for ANY admin PC. The
       commodities catalog (with variety lists per fruit) is normally
       written by ProductRegistry on its first mount, but a fresh
       device may never open that module before going into Shipments
       / Bills / Invoices — and those modules silently fall back to
       empty variety dropdowns when the LS key is missing. Identical
       seed + guard to ShipperPortal.js (~line 2836); kept verbatim
       there so the shipper-side build doesn't depend on App.js
       running first. */
    try {
      var existingCommodities = JSON.parse(localStorage.getItem("gfi-commodities") || "null");
      if (!(Array.isArray(existingCommodities) && existingCommodities.length > 0)) {
        var commoditySeed = [
          { id: 1, nameEn: "Strawberry", nameEs: "Fresa", htsCode: "08101001", taxCode: "50302001", varieties: ["Albion", "Fronteras", "San Andreas"] },
          { id: 2, nameEn: "Blueberry", nameEs: "Arándano", htsCode: "08104002", taxCode: "50302002", varieties: ["Biloxi", "Legacy", "Star"] },
          { id: 3, nameEn: "Blackberry", nameEs: "Zarzamora", htsCode: "08102001", taxCode: "50303003", varieties: ["Tupy", "Kiowa", "Brazos", "Natchez"] },
          { id: 4, nameEn: "Raspberry", nameEs: "Frambuesa", htsCode: "08104003", taxCode: "50302004", varieties: ["Centennial", "Malu", "Driscoll's Maravilla"] },
          { id: 5, nameEn: "Avocado", nameEs: "Aguacate", htsCode: "08044001", taxCode: "50301001", varieties: ["Hass", "Fuerte", "Lamb Hass"] },
          { id: 6, nameEn: "Lime", nameEs: "Limón", htsCode: "08055001", taxCode: "50301005", varieties: ["Persa", "Key Lime"] },
        ];
        localStorage.setItem("gfi-commodities", JSON.stringify(commoditySeed));
      }
    } catch (e) { /* quota / parse — non-fatal */ }
    return () => { alive = false; };
  }, [session]);

  /* ── Backup bookkeeping ──
     installSaveCounter monkey-patches localStorage.setItem so every
     business write to a gfi-* key bumps gfi-saves-since-backup. The
     banner state polls every 60 s so a long session that crosses the
     24 h or N-save threshold prompts the user. */
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupStatus, setBackupStatus] = useState(() => getBackupStatus());
  const [showBackupBanner, setShowBackupBanner] = useState(false);
  useEffect(() => {
    installSaveCounter();
    setShowBackupBanner(shouldPromptForBackup());
    const id = setInterval(() => {
      setBackupStatus(getBackupStatus());
      setShowBackupBanner(shouldPromptForBackup());
    }, 60000);
    return () => clearInterval(id);
  }, []);
  const quickBackup = async () => {
    if (backupBusy) return;
    setBackupBusy(true);
    try { await exportAndDownload({ includeDocs: true }); }
    catch (e) { window.alert("Backup failed: " + (e && e.message ? e.message : e)); }
    setBackupBusy(false);
    setShowBackupBanner(false);
    setBackupStatus(getBackupStatus());
  };
  const dismissBanner = () => {
    dismissBackupReminder();
    setShowBackupBanner(false);
  };

  /* Expose navigation for cross-module use (e.g. Shipment → Bill) */
  useEffect(() => { window.__gfiNavigate = setModule; return () => { delete window.__gfiNavigate; }; }, []);

  /* PalletBreakdown → Shipments deep link: click on a Lot cell dispatches
     this event, we switch to the shipments module and stash the target
     shipmentId for ShipmentRegistry to open once mounted. Remembers the
     module we came from so closing the shipment can return to it. */
  useEffect(() => {
    function onOpenShipment(e) {
      setReturnModule(module);
      setModule('shipments');
      setPendingShipmentId(e.detail && e.detail.shipmentId);
    }
    window.addEventListener('gfi-open-shipment', onOpenShipment);
    return () => window.removeEventListener('gfi-open-shipment', onOpenShipment);
  }, [module]);

  /* Fires when ShipmentRegistry closes a shipment that was opened via
     the deep link above — returns to whatever module we came from. */
  useEffect(() => {
    function onClose() {
      if (returnModule) {
        setModule(returnModule);
        setReturnModule(null);
        setPendingShipmentId(null);
      }
    }
    window.addEventListener('gfi-shipment-closed', onClose);
    return () => window.removeEventListener('gfi-shipment-closed', onClose);
  }, [returnModule]);

  /* Deeplink handler — parses URL hash on mount to route a fresh tab
     directly to a specific record (used by "Open Bill" from the
     Invoice → LOT preview). Format: `#open=<kind>:<id>`, e.g.
     `#open=bill:BILL-0042`. Maps kind → module + global open fn,
     waits 300ms for the target module to mount and register its
     __gfiXxxOpen hook, then opens the record. The hash is cleared
     afterward so a page refresh doesn't re-trigger the same action. */
  useEffect(() => {
    var m = (window.location.hash || "").match(/^#open=([a-z]+):(.+)$/i);
    if (!m) return;
    var moduleMap = { bill: "bills", invoice: "invoices", shipment: "shipments", client: "clients", payment: "payments" };
    var openMap = { bill: "__gfiBillOpen", invoice: "__gfiInvoiceOpen", shipment: "__gfiShipmentOpen", client: "__gfiClientOpen", payment: "__gfiPaymentOpen" };
    var kind = m[1].toLowerCase();
    var id = decodeURIComponent(m[2]);
    if (!moduleMap[kind]) return;
    setModule(moduleMap[kind]);
    setTimeout(function () {
      var fn = window[openMap[kind]];
      if (typeof fn === "function") fn(id);
    }, 300);
    try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch (e) {}
  }, []);

  const greeting = lang === 'es' ? 'Bienvenido,' : 'Welcome,';

  // Clock
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const fmtTime = (tz) => now.toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: true });
  const fmtDate = () => now.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'long', day: '2-digit', year: 'numeric' });
  /* ISO 8601 week number — Monday-start weeks. Matches the W## convention
     used by ProfitTracker, BillRegistry, SeasonRegistry, etc. */
  const fmtWeek = () => {
    const d = new Date(now.getTime()); d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const w1 = new Date(d.getFullYear(), 0, 4);
    return 'Week ' + String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, '0');
  };
  const [userLocation, setUserLocation] = useState('');
  useEffect(() => {
    try { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; const city = tz.split('/').pop().replace(/_/g, ' '); setUserLocation(city); } catch { setUserLocation(''); }
  }, []);

  /* Pending shipper-portal submissions — count of proformas in
     `shipper_shipments` with status="submitted". Drives the red
     badge on the Operations top-nav trigger so the admin sees new
     pending reviews at a glance even when they're on another
     module. CRITICAL: must read from Supabase, NOT localStorage —
     the shipper user submits from their own browser, so the
     admin's LS never sees it. */
  const [pendingPortalCount, setPendingPortalCount] = useState(0);
  useEffect(() => {
    if (!profile || profile.role === 'shipper') return;
    let cancelled = false;
    const loadCount = () => {
      loadFromSupabase('shipper_shipments', { force: true })
        .then((embs) => {
          if (cancelled) return;
          let n = 0;
          (embs || []).forEach((e) => {
            (e.proformas || []).forEach((p) => {
              if ((p.status || 'draft') === 'submitted') n++;
            });
          });
          setPendingPortalCount(n);
        })
        .catch(() => { /* swallow — badge just won't update */ });
    };
    loadCount();
    /* Re-poll on tab focus (catches submissions made while the admin
       was idle) plus a 60s heartbeat (catches submissions made while
       the admin is actively working in another module). */
    const onFocus = () => loadCount();
    window.addEventListener('focus', onFocus);
    const poll = setInterval(loadCount, 60000);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); clearInterval(poll); };
  }, [profile]);

  /* Which top-nav group contains the currently active module?
     Includes SHIPPER_PORTAL_GROUP so the right-side dropdown
     gets the .active highlight when the user is inside it. */
  const activeGroupId = (() => {
    for (const g of NAV) {
      if (g.key === module) return g.id;
      if (g.items && g.items.some(i => i.key === module)) return g.id;
    }
    if (SHIPPER_PORTAL_GROUP.items.some(i => i.key === module)) return SHIPPER_PORTAL_GROUP.id;
    if (SWEETLEAF_GROUP.items.some(i => i.key === module)) return SWEETLEAF_GROUP.id;
    return null;
  })();

  const renderModule = () => {
    switch (module) {
      case 'dashboard': return <Dashboard lang={lang} />;
      case 'products': return <ProductRegistry lang={lang} />;
      case 'suppliers': return <SupplierRegistry lang={lang} />;
      case 'clients': return <ClientRegistry lang={lang} />;
      case 'warehouses': return <WarehouseRegistry lang={lang} />;
      case 'brokers': return <CustomsBrokerRegistry lang={lang} />;
      case 'quality_control': return <QualityControlRegistry lang={lang} />;
      case 'certifiers': return <CertifierRegistry lang={lang} />;
      case 'growers': return <GrowerRegistry lang={lang} />;
      case 'certifications': return <CertificationRegistry lang={lang} />;
      case 'seasons': return <SeasonRegistry lang={lang} />;
      case 'export_certs': return <ExportCertificateRegistry lang={lang} />;
      case 'shipments': return <ShipmentRegistry lang={lang} openShipmentId={pendingShipmentId} onShipmentOpened={() => setPendingShipmentId(null)} />;
      case 'pallet_breakdown': return <PalletBreakdown lang={lang} />;
      case 'gps_tracking': return <GPSTracking lang={lang} />;
      case 'shipper_shipments': return <AdminShipperPortal lang={lang} profile={profile} />;
      case 'shipper_gps': return <GPSTracking lang={lang} />;
      case 'shipper_brokers': return <ShipperBrokersPortal lang={lang} />;
      case 'shipper_settings': return <ShipperSettings lang={lang} />;
      case 'bills': return <BillRegistry lang={lang} />;
      case 'expense_bills': return <ExpenseBillRegistry lang={lang} />;
      case 'fob_prices': return <FobPricesRegistry lang={lang} />;
      case 'forecast': return <ForecastRegistry lang={lang} />;
      case 'weather_watch': return <WeatherWatchRegistry lang={lang} />;
      case 'market_news': return <MarketNewsRegistry lang={lang} />;
      case 'holiday_impact': return <HolidayImpactRegistry lang={lang} />;
      case 'currency_fuel': return <CurrencyFuelRegistry lang={lang} />;
      case 'ai_analyst_log': return <AIAnalystLog lang={lang} />;
      case 'accuracy_dashboard': return <AccuracyDashboard lang={lang} />;
      case 'api_settings': return <ApiSettingsRegistry lang={lang} />;
      case 'report_sales': return <SalesReports lang={lang} />;
      case 'report_generator': return <ReportGenerator lang={lang} />;
      case 'settlements': return <Settlements lang={lang} />;
      case 'storage': return <StorageManager lang={lang} />;
      case 'backup': return <BackupRestore lang={lang} />;
      case 'user_management': return <UserManagement lang={lang} />;
      case 'company_profile': return <CompanyProfile lang={lang} />;
      case 'invoices': return <InvoiceRegistry lang={lang} />;
      case 'sl_invoices': return <SweetLeafPortal lang={lang} />;
      case 'sl_payments': return <SweetLeafPortal lang={lang} initialTab="payments" />;
      case 'sl_settings': return <SweetLeafPortal lang={lang} initialTab="settings" />;
      case 'payments': return <PaymentRegistry lang={lang} />;
      case 'profit_tracking': return <ProfitTracker lang={lang} />;
      case 'fee_catalog': return <FeeCatalog lang={lang} />;
      case 'fixed_pricing': return <FixedPricingRegistry lang={lang} />;
      case 'consignment_pricing': return <ConsignmentPricingRegistry lang={lang} />;
      case 'combined_summary': return <CombinedWeeklySummary lang={lang} />;
      case 'season_comparison': return <SeasonComparison lang={lang} />;
      case 'import_excel': return <ImportFromExcel lang={lang} />;
      case 'supabase_migration': return <SupabaseMigration lang={lang} />;
      case 'report_organic': return <OrganicAuditRegistry lang={lang} />;
      case 'usda_lookup': return <USDALookup lang={lang} />;
      default: {
        const item = NAV_FLAT[module];
        return <ComingSoon label={item ? (lang === 'es' ? item.es : item.en) : module} />;
      }
    }
  };

  /* Logout flow: button opens the confirm modal; only after the
     user clicks "yes" do we actually sign out. */
  const handleLogout = () => setLogoutConfirmOpen(true);
  const confirmLogout = async () => {
    setLogoutConfirmOpen(false);
    try { await supabase.auth.signOut(); } catch (e) { /* ignore */ }
  };

  /* Public /register route — accessible without login. Shows the
     RegisterPage if the URL matches /register (with optional ?token).
     Must be checked BEFORE the auth gate because the user isn't
     logged in yet at this point. */
  if ((window.location.pathname || "").toLowerCase() === "/register") {
    return <RegisterPage lang={lang} setLang={setLang} />;
  }

  if ((window.location.pathname || "").toLowerCase() === "/reset-password") {
    return <PasswordResetPage lang={lang} setLang={setLang} />;
  }

  if ((window.location.pathname || "").toLowerCase() === "/verify-email") {
    return <VerifyEmailPage lang={lang} setLang={setLang} />;
  }

  /* Recovery flow wins over every other gate. The user clicked the
     password-reset link and must set a new password before doing
     anything else. */
  if (recoveryMode) {
    return <ResetPasswordPage lang={lang} onDone={() => {
      setRecoveryMode(false);
      try { window.history.replaceState(null, '', window.location.pathname); } catch (e) {}
    }} />;
  }

  /* Auth gate — while we resolve the initial session show a quiet
     splash; if there's no session, swap the whole shell for LoginPage. */
  if (session === undefined) {
    return (
      <div style={{
        minHeight: '100vh', width: '100%',
        background: 'linear-gradient(135deg, #0D1B2A 0%, #152A40 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 500,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {lang === 'es' ? 'Cargando…' : 'Loading…'}
      </div>
    );
  }
  if (session === null) {
    return <LoginPage lang={lang} setLang={setLang} />;
  }

  /* Role gate: while we fetch the user_profiles row show the same
     splash. Once resolved, a shipper account swaps the entire shell
     for ShipperPortalLanding — shippers never see the admin nav.
     A `null` profile (table missing, RLS denied) falls through to
     the admin shell so the existing app keeps working during the
     migration window before user_profiles is provisioned. */
  if (profile === undefined) {
    return (
      <div style={{
        minHeight: '100vh', width: '100%',
        background: 'linear-gradient(135deg, #0D1B2A 0%, #152A40 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 500,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {lang === 'es' ? 'Cargando…' : 'Loading…'}
      </div>
    );
  }
  /* Non-admin roles all go to the shipper portal. Only 'admin' falls
     through to the full Nexus shell below. The legacy 'shipper' role
     stays supported for any existing accounts that pre-date the
     logistica/operacion/gerencial roles introduced when invitations
     replaced direct user creation. */
  if (profile && profile.role === 'audit_advisor') {
    return <AdvisorPortalShell lang={lang} profile={profile} />;
  }
  if (profile && profile.role && profile.role !== 'admin') {
    return <ShipperPortalShell lang={lang} profile={profile} />;
  }

  return (
    <div className="app-shell-h">
      {/* WATERMARK */}
      <div className="watermark" />

      <header className="top-nav">
        {/* Icon-only brand mark, 2026-09-12 per Arturo — the "GFI Nexus"
           wordmark, "Ground-Fresh International" name, and company logo
           image that used to sit next to this were all removed/moved
           out to free width for the menu; "Ground-Fresh International"
           now lives in the greeting bar instead (main-header-greeting,
           below). Still clickable through to Dashboard, just compact. */}
        <div onClick={() => setModule('dashboard')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginRight: 10, flexShrink: 0 }} title={lang === 'es' ? 'Ir al Dashboard' : 'Go to Dashboard'}>
          <GFILogo />
        </div>

        {/* Hamburger — CSS-only hidden on desktop (.mobile-menu-btn has
            display:none above 900px), shown below that breakpoint in
            place of the now-hidden .top-nav-menu / SweetLeaf / Shipper
            Portal triggers. Opens with the group containing the current
            module already expanded so navigating back into the drawer
            doesn't lose context. */}
        <button
          className="mobile-menu-btn"
          onClick={() => { setMobileExpandedGroup(activeGroupId); setMobileMenuOpen(true); }}
          aria-label={lang === 'es' ? 'Abrir menú' : 'Open menu'}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        <nav className="top-nav-menu" onMouseLeave={scheduleNavClose} onMouseEnter={cancelNavClose}>
          {NAV.map(group => {
            const label = lang === 'es' ? group.es : group.en;
            const isActive = activeGroupId === group.id;
            /* Home / GPS Tracking / any other flat entry: no dropdown.
               Entries with an `icon` field render with the icon as
               leading content and a tooltip-title (so the icon button
               stays compact). */
            if (!group.items) {
              const tooltip = group.icon
                ? (lang === 'es' ? (group.title_es || group.es) : (group.title_en || group.en))
                : undefined;
              return (
                <button
                  key={group.id}
                  className={`nav-trigger ${isActive ? 'active' : ''}`}
                  onClick={() => { setModule(group.key); setOpenDropdown(null); }}
                  onMouseEnter={() => setOpenDropdown(null)}
                  title={tooltip}
                  style={group.icon ? { display: 'inline-flex', alignItems: 'center', gap: 6 } : undefined}
                >
                  {group.icon}
                  <span>{label}</span>
                </button>
              );
            }
            const isOpen = openDropdown === group.id;
            /* Operations group carries the pending-review badge. The
               counter reflects shipper-portal proformas with status
               "submitted" — the admin's queue for accept / reject. */
            const showGroupBadge = group.id === 'operations' && pendingPortalCount > 0;
            return (
              <div
                key={group.id}
                ref={el => { if (el) navTriggerRefs.current[group.id] = el; else delete navTriggerRefs.current[group.id]; }}
                className={`nav-group ${isActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                onMouseEnter={() => { cancelNavClose(); setOpenDropdown(group.id); }}
              >
                {/* onClick toggle is the touch-device path — onMouseEnter
                    above handles desktop hover. Without the click toggle,
                    iOS / Android PWA users tapping a group can't open the
                    dropdown and get stuck on Home (Dashboard). */}
                <button
                  className="nav-trigger"
                  onClick={() => setOpenDropdown(prev => prev === group.id ? null : group.id)}
                  style={showGroupBadge ? { position: 'relative' } : undefined}
                  title={showGroupBadge ? (lang === 'es' ? pendingPortalCount + ' embarque(s) pendientes de revisión' : pendingPortalCount + ' embarque(s) pending review') : undefined}
                >
                  {label}
                  <span className="nav-caret">▾</span>
                  {showGroupBadge && (
                    <span style={{ position: 'absolute', top: 4, right: 2, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: '#DC2626', color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }}>
                      {pendingPortalCount > 99 ? '99+' : pendingPortalCount}
                    </span>
                  )}
                </button>
                {/* Dropdown portaled to document.body so it escapes
                    `.top-nav`'s sticky stacking context AND
                    `.main-body`'s `isolation: isolate`. Same pattern
                    DraggableModal / BulkDeleteDialog already use to
                    survive backdrop-filter / isolation parents. */}
                {isOpen && navTriggerRefs.current[group.id] && (function () {
                  const r = navTriggerRefs.current[group.id].getBoundingClientRect();
                  const isMobile = window.innerWidth < 900;
                  /* Dropdown sits flush against the trigger (no gap)
                     so cursor can land on the dropdown immediately
                     after leaving the button. A taller invisible
                     `bridge` div inside the panel extends the hover
                     zone 12 px above to absorb sub-pixel jitter. */
                  const posStyle = isMobile
                    ? { position: 'fixed', top: 64, left: 12, right: 12, zIndex: 30000, display: 'block', animation: 'navDropFade 0.15s ease' }
                    : { position: 'fixed', top: r.bottom, left: r.left, zIndex: 30000, display: 'block', animation: 'navDropFade 0.15s ease' };
                  return createPortal(
                    <div
                      className="nav-dropdown"
                      style={posStyle}
                      onMouseEnter={cancelNavClose}
                      onMouseLeave={scheduleNavClose}
                    >
                      {/* Invisible hover-bridge — covers any visual
                          gap above the dropdown so the cursor never
                          falls between trigger and panel. */}
                      <div aria-hidden="true" style={{ position: 'absolute', top: -12, left: 0, right: 0, height: 12, background: 'transparent', pointerEvents: 'auto' }} />
                      {group.items.map(item => {
                        const showItemBadge = item.key === 'shipments' && pendingPortalCount > 0;
                        return (
                          <button
                            key={item.key}
                            className={`nav-item ${module === item.key ? 'active' : ''}`}
                            onClick={() => { setModule(item.key); setOpenDropdown(null); }}
                            style={showItemBadge ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } : undefined}
                          >
                            <span>{lang === 'es' ? item.es : item.en}</span>
                            {showItemBadge && (
                              <span style={{ minWidth: 18, height: 16, padding: '0 5px', borderRadius: 8, background: '#DC2626', color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center', marginLeft: 8 }}>
                                {pendingPortalCount > 99 ? '99+' : pendingPortalCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>,
                    document.body
                  );
                })()}
              </div>
            );
          })}
        </nav>

        <div className="top-nav-right">
          {/* SweetLeaf — sister-brand portal. Sits before the Shipper
              Portal group per UX spec. Uses the SL isotype as a leading
              icon on the trigger label. */}
          {(function () {
            var g = SWEETLEAF_GROUP;
            var label = lang === 'es' ? g.es : g.en;
            var isActive = activeGroupId === g.id;
            var isOpen = openDropdown === g.id;
            return (
              <div
                ref={el => { if (el) navTriggerRefs.current[g.id] = el; else delete navTriggerRefs.current[g.id]; }}
                className={`nav-group ${isActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                onMouseEnter={() => { cancelNavClose(); setOpenDropdown(g.id); }}
                onMouseLeave={scheduleNavClose}
                style={{ marginRight: 6 }}
              >
                <button
                  className="nav-trigger"
                  onClick={() => setOpenDropdown(prev => prev === g.id ? null : g.id)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  title={label}
                >
                  <img src={SL_ISOTIPO_B64} alt={label} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                  <span className="nav-caret">▾</span>
                </button>
                {isOpen && navTriggerRefs.current[g.id] && (function () {
                  const r = navTriggerRefs.current[g.id].getBoundingClientRect();
                  const isMobile = window.innerWidth < 900;
                  const posStyle = isMobile
                    ? { position: 'fixed', top: 64, left: 12, right: 12, zIndex: 30000, display: 'block', animation: 'navDropFade 0.15s ease' }
                    : { position: 'fixed', top: r.bottom, right: Math.max(8, window.innerWidth - r.right), zIndex: 30000, display: 'block', animation: 'navDropFade 0.15s ease' };
                  return createPortal(
                    <div
                      className="nav-dropdown"
                      style={posStyle}
                      onMouseEnter={cancelNavClose}
                      onMouseLeave={scheduleNavClose}
                    >
                      <div aria-hidden="true" style={{ position: 'absolute', top: -12, left: 0, right: 0, height: 12, background: 'transparent', pointerEvents: 'auto' }} />
                      {g.items.map(item => (
                        <button
                          key={item.key}
                          className={`nav-item ${module === item.key ? 'active' : ''}`}
                          onClick={() => { setModule(item.key); setOpenDropdown(null); }}
                        >
                          {lang === 'es' ? item.es : item.en}
                        </button>
                      ))}
                    </div>,
                    document.body
                  );
                })()}
              </div>
            );
          })()}
          {/* Shipper Portal — independent dropdown anchored to the
              icon strip (not the main menu). Right-anchored so the menu
              opens to the LEFT and doesn't clip off-screen. */}
          {(function () {
            var g = SHIPPER_PORTAL_GROUP;
            var label = lang === 'es' ? g.es : g.en;
            var isActive = activeGroupId === g.id;
            var isOpen = openDropdown === g.id;
            return (
              <div
                ref={el => { if (el) navTriggerRefs.current[g.id] = el; else delete navTriggerRefs.current[g.id]; }}
                className={`nav-group ${isActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                onMouseEnter={() => { cancelNavClose(); setOpenDropdown(g.id); }}
                onMouseLeave={scheduleNavClose}
                style={{ marginRight: 6 }}
              >
                <button
                  className="nav-trigger"
                  onClick={() => setOpenDropdown(prev => prev === g.id ? null : g.id)}
                >
                  {label}
                  <span className="nav-caret">▾</span>
                </button>
                {/* Right-anchored portal — pins the dropdown's right
                    edge to the trigger's right edge so the menu opens
                    leftward (the icon strip sits at the far right of
                    the navbar; a left-anchored portal would clip off
                    screen). Mobile still uses the full-width pin. */}
                {isOpen && navTriggerRefs.current[g.id] && (function () {
                  const r = navTriggerRefs.current[g.id].getBoundingClientRect();
                  const isMobile = window.innerWidth < 900;
                  const posStyle = isMobile
                    ? { position: 'fixed', top: 64, left: 12, right: 12, zIndex: 30000, display: 'block', animation: 'navDropFade 0.15s ease' }
                    : { position: 'fixed', top: r.bottom, right: Math.max(8, window.innerWidth - r.right), zIndex: 30000, display: 'block', animation: 'navDropFade 0.15s ease' };
                  return createPortal(
                    <div
                      className="nav-dropdown"
                      style={posStyle}
                      onMouseEnter={cancelNavClose}
                      onMouseLeave={scheduleNavClose}
                    >
                      <div aria-hidden="true" style={{ position: 'absolute', top: -12, left: 0, right: 0, height: 12, background: 'transparent', pointerEvents: 'auto' }} />
                      {g.items.map(item => (
                        <button
                          key={item.key}
                          className={`nav-item ${module === item.key ? 'active' : ''}`}
                          onClick={() => { setModule(item.key); setOpenDropdown(null); }}
                        >
                          {lang === 'es' ? item.es : item.en}
                        </button>
                      ))}
                    </div>,
                    document.body
                  );
                })()}
              </div>
            );
          })()}
          {/* Quick-backup button — one click downloads gfi-backup-*.json
              with every gfi-* localStorage key + IndexedDB document.
              Turns red when a backup is overdue (15+ saves or 24+ h
              since the last one). */}
          <button
            className="top-nav-search-btn"
            onClick={quickBackup}
            disabled={backupBusy}
            title={(lang === 'es' ? 'Respaldar ahora · Último: ' : 'Backup now · Last: ') + (backupStatus.lastIso ? new Date(backupStatus.lastIso).toLocaleString() : (lang === 'es' ? 'Nunca' : 'Never')) + (backupStatus.saves > 0 ? ' · ' + backupStatus.saves + (lang === 'es' ? ' guardados pendientes' : ' pending saves') : '')}
            aria-label="Backup now"
            style={{ color: showBackupBanner ? '#DC2626' : undefined }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>

          <button
            className="top-nav-search-btn"
            onClick={() => setSearchOpen(true)}
            title={lang === 'es' ? 'Buscar (lupa)' : 'Search'}
            aria-label="Search"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          <div className="top-nav-create-wrap" ref={createRef}>
            <button
              className={`top-nav-create-btn ${createOpen ? 'active' : ''}`}
              onClick={() => setCreateOpen(v => !v)}
              title={lang === 'es' ? 'Crear nuevo' : 'Create new'}
              aria-label="Create new"
              aria-expanded={createOpen}
            >+</button>
            {createOpen && (
              <div className="top-nav-create-menu" role="menu">
                <div className="top-nav-create-label">{lang === 'es' ? 'CREAR NUEVO' : 'CREATE NEW'}</div>
                {CREATE_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    className="nav-item"
                    role="menuitem"
                    onClick={() => goCreate(opt.key, opt.hook)}
                  >
                    {lang === 'es' ? opt.es : opt.en}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="top-nav-search-btn"
            onClick={handleLogout}
            title={(lang === 'es' ? 'Cerrar sesión' : 'Logout') + (session && session.user && session.user.email ? ' · ' + session.user.email : '')}
            aria-label="Logout"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>

        </div>
      </header>

      {/* Mobile nav drawer — portaled to document.body for the same
          reason the desktop dropdowns are (escapes .top-nav's sticky +
          .main-body's isolation stacking contexts, see navTriggerRefs
          comment above). Every group (main NAV + SweetLeaf + Shipper
          Portal) as a tap-to-expand accordion; Dashboard as a flat
          top-level item since it has no sub-items. */}
      {mobileMenuOpen && createPortal(
        <div className="mobile-nav-backdrop" onClick={() => { setMobileMenuOpen(false); setMobileExpandedGroup(null); }}>
          <div className="mobile-nav-drawer" onClick={e => e.stopPropagation()}>
            <div className="mobile-nav-drawer-header">
              <span>{lang === 'es' ? 'Menú' : 'Menu'}</span>
              <button
                className="mobile-nav-close-btn"
                onClick={() => { setMobileMenuOpen(false); setMobileExpandedGroup(null); }}
                aria-label={lang === 'es' ? 'Cerrar menú' : 'Close menu'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="mobile-nav-drawer-body">
              <button
                className={`mobile-nav-flat-item ${module === 'dashboard' ? 'active' : ''}`}
                onClick={() => { setModule('dashboard'); setMobileMenuOpen(false); setMobileExpandedGroup(null); }}
              >
                {lang === 'es' ? 'Dashboard' : 'Dashboard'}
              </button>
              {[...NAV.filter(g => g.items), SWEETLEAF_GROUP, SHIPPER_PORTAL_GROUP].map(group => {
                const label = lang === 'es' ? group.es : group.en;
                const expanded = mobileExpandedGroup === group.id;
                const showGroupBadge = group.id === 'operations' && pendingPortalCount > 0;
                return (
                  <div key={group.id} className="mobile-nav-group">
                    <button
                      className={`mobile-nav-group-trigger ${activeGroupId === group.id ? 'active' : ''}`}
                      onClick={() => setMobileExpandedGroup(prev => prev === group.id ? null : group.id)}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {label}
                        {showGroupBadge && (
                          <span style={{ minWidth: 18, height: 16, padding: '0 5px', borderRadius: 8, background: '#DC2626', color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center' }}>
                            {pendingPortalCount > 99 ? '99+' : pendingPortalCount}
                          </span>
                        )}
                      </span>
                      <span className={`mobile-nav-chevron ${expanded ? 'open' : ''}`}>▾</span>
                    </button>
                    {expanded && (
                      <div className="mobile-nav-group-items">
                        {group.items.map(item => {
                          const showItemBadge = item.key === 'shipments' && pendingPortalCount > 0;
                          return (
                            <button
                              key={item.key}
                              className={`mobile-nav-item ${module === item.key ? 'active' : ''}`}
                              onClick={() => { setModule(item.key); setMobileMenuOpen(false); setMobileExpandedGroup(null); }}
                            >
                              <span>{lang === 'es' ? item.es : item.en}</span>
                              {showItemBadge && (
                                <span style={{ minWidth: 18, height: 16, padding: '0 5px', borderRadius: 8, background: '#DC2626', color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center', marginLeft: 8 }}>
                                  {pendingPortalCount > 99 ? '99+' : pendingPortalCount}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className={"main-content-h" + (module === "shipments" || module === "bills" || module === "invoices" || module === "expense_bills" || module === "seasons" || module === "pallet_breakdown" ? " main-content-h--square" : "")}>
        <div className="main-header-h">
          {/* 2026-10-20, Arturo: "welcome arturo muevelo despues de
              ground-fresh international" — same line, right after,
              instead of stacked on its own row below. */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          {/* 2026-10-20, Arturo: "mismo font de CDMX" — fontSize 12,
              weight 500, var(--color-text-secondary), same as the
              CDMX/PST clock labels. */}
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)' }}>Ground-Fresh International</div>
          <div className="main-header-greeting">{greeting} <strong>Arturo</strong></div>
        </div>
          <div className="header-info-row" style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: 12, fontWeight: 500, marginLeft: 'auto' }}>
            {userLocation && <span className="header-location" style={{ color: 'var(--color-text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {userLocation}
            </span>}
            {/* 2026-10-20, Arturo: "toda esa seccion utiliza el font
                de new york y sin negritas" — same plain style as the
                "New York" location span (fontSize 12, default family,
                no bold) across date/week/clocks. */}
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 12, fontWeight: 500 }}>{fmtDate()}</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)' }}>{fmtWeek()}</span>
            <div className="header-clocks" style={{ display: 'flex', gap: 14 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)', opacity: 0.7 }}>CDMX</span>
                <span style={{ color: 'var(--color-text-secondary)', fontSize: 12, fontWeight: 500 }}>{fmtTime('America/Mexico_City')}</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)', opacity: 0.7 }}>PST</span>
                <span style={{ color: 'var(--color-text-secondary)', fontSize: 12, fontWeight: 500 }}>{fmtTime('America/Los_Angeles')}</span>
              </span>
            </div>
            <div className="header-lang-switch">
              {['en', 'es'].map(l => (
                <button key={l} className={lang === l ? 'active' : ''} onClick={() => setLang(l)}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* 2026-10-20, Arturo: "el exterior de esta ventana de
            embarques no debe ser redondeado" — Shipments-only modifier
            class, same scoping pattern as titleBarVariant="groundfresh"
            for the shipment detail modal; the plain .main-body rule
            (shared by every module) stays untouched. */}
        <div className={"main-body" + (module === "shipments" || module === "bills" || module === "invoices" || module === "expense_bills" || module === "seasons" || module === "pallet_breakdown" ? " main-body--square" : "")}>
          {showBackupBanner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', marginBottom: 12, fontSize: 12, fontWeight: 600, color: '#B91C1C' }}>
              <span style={{ fontSize: 16 }}>⚠</span>
              <span style={{ flex: 1 }}>
                {lang === 'es'
                  ? 'Llevas ' + backupStatus.saves + ' guardados sin respaldo' + (backupStatus.lastIso ? ' (último hace ' + Math.round(backupStatus.hoursSince) + ' h)' : ' (nunca se ha respaldado)') + '. ¿Descargar respaldo?'
                  : backupStatus.saves + ' saves since last backup' + (backupStatus.lastIso ? ' (last ' + Math.round(backupStatus.hoursSince) + ' h ago)' : ' (never backed up)') + '. Download backup?'}
              </span>
              <button onClick={quickBackup} disabled={backupBusy} style={{ padding: '6px 14px', borderRadius: 5, border: 'none', background: '#DC2626', color: '#fff', cursor: backupBusy ? 'wait' : 'pointer', fontSize: 11, fontWeight: 700 }}>
                {backupBusy ? (lang === 'es' ? 'Preparando…' : 'Building…') : (lang === 'es' ? '💾 Respaldar ahora' : '💾 Backup now')}
              </button>
              <button onClick={() => { setModule('backup'); }} style={{ padding: '6px 12px', borderRadius: 5, border: '1px solid rgba(220,38,38,0.3)', background: 'transparent', color: '#B91C1C', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                {lang === 'es' ? 'Abrir módulo' : 'Open module'}
              </button>
              <button onClick={dismissBanner} title={lang === 'es' ? 'Ocultar por 4 h' : 'Dismiss for 4 h'} style={{ padding: '6px 10px', borderRadius: 5, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                ✕
              </button>
            </div>
          )}
          {renderModule()}
        </div>
      </div>

      {logoutConfirmOpen && createPortal(
        <div
          onClick={() => setLogoutConfirmOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 40000,
            background: 'rgba(13,27,42,0.65)',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, animation: 'navDropFade 0.15s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 380, padding: '28px 28px 22px',
              borderRadius: 14,
              background: 'linear-gradient(135deg, #0D1B2A 0%, #152A40 100%)',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              color: '#fff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'rgba(91,188,176,0.15)',
                border: '1px solid rgba(91,188,176,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#5BBCB0', flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '0.01em', fontFamily: "'DM Sans', sans-serif" }}>
                {lang === 'es' ? '¿Cerrar sesión?' : 'Sign out?'}
              </h2>
            </div>
            <p style={{ margin: '0 0 22px', fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>
              {lang === 'es'
                ? 'Vas a cerrar sesión en GFI Nexus. Tendrás que volver a iniciar sesión para entrar de nuevo.'
                : 'You are about to sign out of GFI Nexus. You will need to sign in again to access the app.'}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setLogoutConfirmOpen(false)}
                style={{
                  padding: '10px 18px', borderRadius: 8,
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  letterSpacing: '0.02em',
                }}
              >
                {lang === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={confirmLogout} autoFocus
                style={{
                  padding: '10px 18px', borderRadius: 8, border: 'none',
                  background: '#5BBCB0', color: '#0D1B2A',
                  fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                  letterSpacing: '0.02em',
                  boxShadow: '0 4px 14px rgba(91,188,176,0.25)',
                }}
              >
                {lang === 'es' ? 'Cerrar sesión' : 'Sign out'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {searchOpen && <GlobalSearch lang={lang} onClose={() => setSearchOpen(false)} />}
    </div>
  );
}

export default App;